import React from 'react'
import PropTypes from 'prop-types'
import ReactProgressiveImage from 'react-progressive-image'
import isUndefined from 'lodash/isUndefined'
import { Waypoint } from 'react-waypoint'
import withCommonSnapshotsOnly from '@signal/hoc/withCommonSnapshotsOnly'
import publicUrl from '@signal/utils/publicUrl'

export default class ProgressiveImage extends ReactProgressiveImage {
  constructor(props) {
    super(props)

    this.loadImageSuper = this.loadImage
    this.loadImage = (src, srcSetData) => {
      if (isUndefined(src)) return
      this.loadImageSuper(src, srcSetData)
    }
  }

  static defaultProps = {
    placeholder: publicUrl('/images/img-placeholder.svg')
  }
}

/**
 * @global
 * This object tracks the image srcs that have been previously loaded before.
 * it is used inside of LazyProgressiveImage to determine if the image can be
 * loaded from cache.
 */
let imageHasLoadedBefore
if (!imageHasLoadedBefore) {
  imageHasLoadedBefore = {}
}

/**
 * This component lazy loads images based on when they enter into the viewport.
 * When the component enters into the viewport, a timout is set for the given delay.
 * If the component exists the viewport before the timeout is finished, the timeout
 * is the cancelled. If the component is still inside of the viewport when the
 * timeout is finished, then it switches the src of the wrapped Progressive image
 * to the actual image src (from the placeholder). If, when the component enters
 * into the viewport the image has been previously loaded (determined by the global cache
 * object `imageHasLoadedBefore`) then this delay is bypassed and the image loads immediately.
 */
class _LazyProgressiveImage extends React.Component {
  state = {
    inView: false,
    loadImage: imageHasLoadedBefore[this.props.src] ? true : false,
    cancel: null
  }

  static propTypes = {
    delay: PropTypes.number
  }

  static defaultProps = {
    delay: 500
  }

  // eslint-disable-next-line no-unused-vars
  shouldComponentUpdate(nextProps, nextState) {
    /**
     * This component only needs to re-render when the state changes from not
     * loading the image to loading the image. All other changes from setState
     * do not require a re-render. It's also possible that on instantiation,
     * loadImage will be true, in which case it is also not necessary to ever
     * re-render this component.
     */
    return !this.state.loadImage && nextState.loadImage
  }

  get progressiveImageProps() {
    const { src, placeholder, ...rest } = this.props
    const { loadImage } = this.state

    // Since we're about to load the image, save it to the global cache object
    if (loadImage) imageHasLoadedBefore[src] = true

    return {
      // return the placeholder for the image if it's not ready to load yet
      src: loadImage ? src : placeholder,
      placeholder,
      ...rest
    }
  }

  componentWillUnmount() {
    const { cancel } = this.state
    if (cancel) cancel()
  }

  loadImage = () => {
    const { cancel, loadImage } = this.state
    if (cancel || loadImage) return

    const { delay } = this.props
    const timeout = setTimeout(() => this.setState({ loadImage: true }), delay)
    this.setState({
      cancel: () => clearTimeout(timeout)
    })
  }

  handleLeave = () => {
    const { cancel } = this.state
    const newState = {}
    if (cancel) {
      cancel()
      newState.cancel = null
    }
    newState.inView = false
    this.setState(newState)
  }

  render() {
    /**
     * I attempted to remove the waypoint when loadImage === true because
     * it would no longer be necessary, however, it messed up the heights of the
     * react-virtualized list where it is currently, primarily used.
     */
    return [
      <Waypoint
        bottomOffset="-750px"
        topOffset="-750px"
        key="waypoint"
        scrollableAncestor="window"
        onEnter={this.loadImage}
        onLeave={this.handleLeave}
      />,
      <ProgressiveImage key="image" {...this.progressiveImageProps} />
    ]
  }
}

export const LazyProgressiveImage = withCommonSnapshotsOnly(
  _LazyProgressiveImage
)
